/*
  Desglose de piezas de la pre cotización: qué piezas van en cada placa (renglón de la
  cotización previa). Lógica pura, sin React ni axios, para que la usen la pantalla y el
  Excel por igual (y se pueda probar sola).

  La pieza apunta a su placa por `renglonClave` = `filasCotizacion[].clave`, no por
  índice: los renglones se reordenan y se borran, y un índice dejaría la pieza pegada a
  la placa equivocada. El PDF (gemelo backend/frontend) repite `etiquetaRenglon` y el
  orden por placa con la misma regla, porque no importa módulos de las pantallas.
*/

const texto = (valor) => (valor === undefined || valor === null ? "" : String(valor).trim());
const numero = (valor) => Number(valor) || 0;

// 0.1 + 0.2 no debe verse como 0.30000000000000004 (mismo criterio que las notas de la OM).
export const redondear2 = (valor) => Math.round(numero(valor) * 100) / 100;
export const formatearCantidad = (valor) => String(redondear2(valor));

export const nuevaClaveRenglon = () =>
  `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// Pre cotizaciones capturadas antes del desglose no traen clave en sus renglones: se
// les pone al abrirlas y se conserva al guardar.
export const asegurarClaves = (filas = []) =>
  filas.map((fila) => (fila && texto(fila.clave) ? fila : { ...(fila || {}), clave: nuevaClaveRenglon() }));

// "#1 · A36 3/4" 4X10ft · CONEXIONES": lo que identifica a la placa para el usuario.
export const etiquetaRenglon = (fila = {}, indice = 0) => {
  const material = [texto(fila.grupo), texto(fila.calibre), texto(fila.dimensiones)].filter(Boolean).join(" ");
  return [`#${indice + 1}`, material, texto(fila.nombreProyecto)].filter(Boolean).join(" · ");
};

export const metrosTotalesPieza = (pieza = {}) => redondear2(numero(pieza.metrosPorPieza) * numero(pieza.cantidad));

// Índice de la placa de cada clave, para ordenar y etiquetar sin recorrer la lista cada vez.
const indicePorClave = (filas = []) => {
  const indices = new Map();
  filas.forEach((fila, indice) => {
    const clave = texto(fila?.clave);
    if (clave && !indices.has(clave)) indices.set(clave, indice);
  });
  return indices;
};

/**
 * Las piezas en el orden de sus placas (y, dentro de cada placa, en el que se
 * capturaron). Las que no tienen placa, o cuya placa ya no existe, van al final con
 * "Sin placa" como material.
 */
export const piezasOrdenadasPorPlaca = (piezas = [], filas = []) => {
  const indices = indicePorClave(filas);
  return (piezas || [])
    .map((pieza, posicion) => {
      const indice = indices.get(texto(pieza?.renglonClave));
      return {
        pieza: pieza || {},
        posicion,
        indice: indice === undefined ? Number.MAX_SAFE_INTEGER : indice,
        material: indice === undefined ? "Sin placa" : etiquetaRenglon(filas[indice], indice),
      };
    })
    .sort((a, b) => a.indice - b.indice || a.posicion - b.posicion);
};

export const totalesPiezas = (piezas = []) => ({
  cantidad: redondear2((piezas || []).reduce((acc, pieza) => acc + numero(pieza?.cantidad), 0)),
  metros: redondear2((piezas || []).reduce((acc, pieza) => acc + metrosTotalesPieza(pieza || {}), 0)),
});

/**
 * Por cada placa que tiene piezas en el desglose: lo capturado en el renglón contra la
 * suma de sus piezas. No corrige nada, solo dice si cuadra (a 2 decimales).
 */
export const cuadrePorRenglon = (filas = [], piezas = []) => {
  const piezasPorClave = new Map();
  (piezas || []).forEach((pieza) => {
    const clave = texto(pieza?.renglonClave);
    if (!clave) return;
    if (!piezasPorClave.has(clave)) piezasPorClave.set(clave, []);
    piezasPorClave.get(clave).push(pieza);
  });

  return (filas || [])
    .map((fila, indice) => ({ fila: fila || {}, indice }))
    .filter(({ fila }) => piezasPorClave.has(texto(fila.clave)))
    .map(({ fila, indice }) => {
      const totales = totalesPiezas(piezasPorClave.get(texto(fila.clave)));
      const piezasCapturadas = redondear2(fila.piezasTotales);
      const metrosCapturados = redondear2(fila.metrosCorte);
      return {
        clave: texto(fila.clave),
        etiqueta: etiquetaRenglon(fila, indice),
        piezasCapturadas,
        piezasDesglose: totales.cantidad,
        metrosCapturados,
        metrosDesglose: totales.metros,
        cuadra: piezasCapturadas === totales.cantidad && metrosCapturados === totales.metros,
      };
    });
};
